import {API} from '@/util/API';
import {SupportedChainId} from '@kei.fi/data';

export interface BalancesResponse {
  balances: Balance[];
}

export interface Balance {
  contract_decimals: number;
  contract_name: string;
  contract_ticker_symbol: string;
  contract_address: string;
  contract_display_name: string;
  supports_erc: string[];
  logo_url: string;
  last_transferred_at: string;
  native_token: boolean;
  type: string;
  is_spam: boolean;
  balance: string;
  balance_24h: string;
  quote_rate: number;
  quote_rate_24h: number;
  quote: number;
  quote_24h: number;
  pretty_quote: string;
  pretty_quote_24h: string;
  logo_urls: {
    token_logo_url: string;
    protocol_logo_url: string;
    chain_logo_url: string;
  };
  protocol_metadata: null;
  nft_data: null;
  id: string;
  symbol: string;
  name: string;
  web_slug: string;
  asset_platform_id: string;
  platforms: {[key: string]: string};
  detail_platforms: {[key: string]: DetailPlatform};
  block_time_in_minutes: number;
  hashing_algorithm: null;
  categories: string[];
  preview_listing: boolean;
  public_notice: string;
  additional_notices: any[];
  localization: {[key: string]: string};
  description: {[key: string]: string};
  links: Links;
  image: {
    thumb: string;
    small: string;
    large: string;
  };
  country_origin: string;
  genesis_date: null;
  sentiment_votes_up_percentage: number;
  sentiment_votes_down_percentage: number;
  watchlist_portfolio_users: number;
  market_cap_rank: number;
  developer_data: DeveloperData;
  status_updates: any[];
  last_updated: string;
}

export interface DetailPlatform {
  decimal_place: number;
  contract_address: string;
}

export interface Links {
  homepage: string[];
  whitepaper: string;
  blockchain_site: string[];
  official_forum_url: string[];
  chat_url: string[];
  announcement_url: string[];
  twitter_screen_name: string;
  facebook_username: string;
  bitcointalk_thread_identifier: null;
  telegram_channel_identifier: string;
  subreddit_url: string;
  repos_url: ReposUrl;
}

export interface ReposUrl {
  github: any[];
  bitbucket: any[];
}

export interface DeveloperData {
  forks: number;
  stars: number;
  subscribers: number;
  total_issues: number;
  closed_issues: number;
  pull_requests_merged: number;
  pull_request_contributors: number;
  code_additions_deletions_4_weeks: {
    additions: null;
    deletions: null;
  };
  commit_count_4_weeks: number;
  last_4_weeks_commit_activity_series: any[];
}

const getAccountBalances = async (
  chainId: SupportedChainId,
  address: string
): Promise<Balance[]> => {
  const {balances} = await API.get<null, BalancesResponse>(
    '/tokens/balances?chainId=' + chainId + '&address=' + address
  );

  return balances;
};

export interface Transfer {
  contractAddress: string;
  contractDecimals: string;
  contractName: string;
  contractSymbol: string;
  fromAddress: string;
  toAddress: string;
  value: string;
  blockHash: string;
  blockNumber: number;
  blockTimestamp: string;
  transactionHash: string;
  transactionIndex: number;
  logIndex: number;
}

export interface Transactions {
  blockHash: string;
  blockNumber: string;
  confirmations: string;
  contractAddress: string;
  cumulativeGasUsed: string;
  from: string;
  functionName: string;
  gas: string;
  gasPrice: string;
  gasPriceBid: string;
  gasUsed: string;
  hash: string;
  input: string;
  isError: string;
  methodId: string;
  nonce: string;
  timeStamp: string;
  to: string;
  transactionIndex: string;
  txreceipt_status: string;
  value: string;
}

export interface GetTransactionsResponse {
  txs: Transactions[];
  internalTxs: Transactions[];
  transfers: Transfer[];
}

const getTransactions = async (
  chainId: SupportedChainId,
  address: string
): Promise<GetTransactionsResponse> => {
  return await API.get<null, GetTransactionsResponse>(
    '/tokens/transactions?chainId=' + chainId + '&address=' + address
  );
};

export interface FullTokenInfo {
  id: string;
  symbol: string;
  name: string;
  web_slug: string;
  asset_platform_id: string;
  platforms: {
    [key: string]: string;
  };
  detail_platforms: {
    [key: string]: {
      decimal_place: number;
      contract_address: string;
    };
  };
  block_time_in_minutes: number;
  hashing_algorithm: null | string;
  categories: string[];
  preview_listing: boolean;
  public_notice: string;
  additional_notices: string[];
  localization: {
    [key: string]: string;
  };
  description: {
    [key: string]: string;
  };
  links: {
    homepage: string[];
    whitepaper: string;
    blockchain_site: string[];
    official_forum_url: string[];
    chat_url: string[];
    announcement_url: string[];
    twitter_screen_name: string;
    facebook_username: string;
    bitcointalk_thread_identifier: null | number;
    telegram_channel_identifier: string;
    subreddit_url: string;
    repos_url: {
      github: string[];
      bitbucket: string[];
    };
  };
  image: {
    thumb: string;
    small: string;
    large: string;
  };
  color?: {
    vibrant?: string;
    muted?: string;
    darkVibrant?: string;
    lightVibrant?: string;
    darkMuted?: string;
    lightMuted?: string;
  };
  country_origin: string;
  genesis_date: null | string;
  contract_address: string;
  sentiment_votes_up_percentage: null | number;
  sentiment_votes_down_percentage: null | number;
  watchlist_portfolio_users: number;
  market_cap_rank: null | number;
  developer_data: {
    forks: number;
    stars: number;
    subscribers: number;
    total_issues: number;
    closed_issues: number;
    pull_requests_merged: number;
    pull_request_contributors: number;
    code_additions_deletions_4_weeks: {
      additions: null | number;
      deletions: null | number;
    };
    commit_count_4_weeks: number;
    last_4_weeks_commit_activity_series: number[];
  };
  status_updates: any[]; // Specify the type based on expected structure.
  last_updated: string;
}

const getTokenInfo = async (
  ...queries: string[]
): Promise<Record<string, FullTokenInfo>> => {
  return await API.get<null, Record<string, FullTokenInfo>>(
    '/tokens/info?queries=' + encodeURI(queries.join(','))
  );
};

const clearCache = async (
  chainId: SupportedChainId,
  address: string
): Promise<Record<string, FullTokenInfo>> => {
  return await API.delete('/tokens/cache', {
    data: {
      chainId,
      address,
    },
  });
};

export const tokensApi = {
  getAccountBalances,
  getTransactions,
  getTokenInfo,
  clearCache,
};
